module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<p>A client needs an application cache to ensure that there is self-consistency throughout the application (and avoid stale data). Doing this requires that the client is able to insert resources into the application state. It is usually the case that a client requires a URI inside the network and must insert it in the correct location for self-consistency. Without this approach each controller has its own copy of a resource. This tutorial demonstrates how use an existing application cache utility (rather than how to build one yourself).</p>\n<p><img src=\"/frontend/vue-todo-app/application-cache.png\" alt=\"application cache\"></p>\n<h3>General guidelines</h3>\n<ul>\n<li>a representation is always placed in relation to the root of the API</li>\n<li>its position is relative to link relations</li>\n<li>retrieving a representation is retrieved relative to the link relations of the context (and default context is root of the API)</li>\n</ul>\n<p>It is quite easy to loose control of this part of the code. One recommendation is to create separate code that uses link relations (the tip here is that semantic link cache import is never in a Vue component). This has multiple benefits: improves understanding of domain (link relations), avoids duplication, improves composition (reusability), improves its changeability and, finally, makes it independently testable. Enough, some code!</p>\n<Instruction>\n<p>Start with a root representation that has at least the ‘self’ link and uri (ie sparsely populated). And yes, we have a single (root/global) cache on <code>this.$root.$api</code>. This will always be invoked before client routing.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-js\"  path=\"...todo-hypermedia/client/src/component/app/App.vue\"  nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">// App.vue</span>\n<span class=\"token operator\">&lt;</span>script<span class=\"token operator\">></span>\n    <span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>query<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'semantic-network'</span><span class=\"token punctuation\">;</span>\n    <span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>apiUri<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'semantic-link-utils/UriMapping'</span><span class=\"token punctuation\">;</span>\n\n    <span class=\"token keyword\">export</span> <span class=\"token keyword\">default</span> <span class=\"token punctuation\">{</span>\n        name<span class=\"token punctuation\">:</span> <span class=\"token string\">'app'</span><span class=\"token punctuation\">,</span>\n\n        created<span class=\"token punctuation\">:</span> <span class=\"token keyword\">function</span> <span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n\n            <span class=\"token comment\" spellcheck=\"true\">/**\n             * The api representation is the 'top of the tree' in terms of the network of\n             * data synchronisation with the server. Note that the in-memory resource does\n             * have an internal {@link State} to track the need for synchronisation throughout\n             * its life (including staleness)\n             *\n             * However, the data is not being fully or reliably propagated down to the children.\n             * Until a better solution is found, we are injecting it from the previous state on the\n             * state change events. See below.\n             *\n             * Note: this is currently found on `this.$root.$api` because it was pre-registered\n             *\n             * @example setup example\n             *\n             *   let store;\n             *\n             *   const apiPlugin = {\n             *       store,\n             *       install(Vue) {\n             *           // attach to the root view\n             *           // access via this.$root.$api\n             *           Vue.prototype.$api = store;\n             *       }\n             *   };\n             *\n             *   Vue.use(apiPlugin);\n             *\n             * @example usage\n             *\n             *  getUri(this.$root.$api, 'self') --> a uri\n             *\n             * @type {ApiRepresentation}\n             */</span>\n            <span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>$root<span class=\"token punctuation\">.</span>$api <span class=\"token operator\">=</span> <span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>$root<span class=\"token punctuation\">.</span>$api <span class=\"token operator\">||</span> query<span class=\"token punctuation\">.</span><span class=\"token function\">create</span><span class=\"token punctuation\">(</span><span class=\"token string\">'HEAD'</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>rel<span class=\"token punctuation\">:</span> <span class=\"token string\">'api'</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n        <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n<span class=\"token operator\">&lt;</span><span class=\"token operator\">/</span>script<span class=\"token operator\">></span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Client routing will now occur and load up a client view. The <code>apiUri</code> always injected into views (by convention of the <code>router</code>).</p>\n<Instruction>\n<p>Load the representation in the client URL into the cache, create reference that the HTML can bind to.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-js\"  path=\"...todo-hypermedia/client/src/components/app/Todo.vue\"  nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">// Todo.vue</span>\n<span class=\"token operator\">&lt;</span>template<span class=\"token operator\">></span>\n        <span class=\"token operator\">...</span>\n        <span class=\"token operator\">&lt;</span>ul <span class=\"token keyword\">class</span><span class=\"token operator\">=</span><span class=\"token string\">\"todo-list\"</span><span class=\"token operator\">></span>\n            <span class=\"token operator\">&lt;</span>li v<span class=\"token operator\">-</span><span class=\"token keyword\">for</span><span class=\"token operator\">=</span><span class=\"token string\">\"(todo, index) in todoCollection\"</span>\n                <span class=\"token operator\">...</span>\n            <span class=\"token operator\">&lt;</span><span class=\"token operator\">/</span>li<span class=\"token operator\">></span>\n        <span class=\"token operator\">&lt;</span><span class=\"token operator\">/</span>ul<span class=\"token operator\">></span>\n<span class=\"token operator\">&lt;</span><span class=\"token operator\">/</span>template<span class=\"token operator\">></span>\n\n<span class=\"token operator\">&lt;</span>script<span class=\"token operator\">></span>\n    <span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>log<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'logger'</span><span class=\"token punctuation\">;</span>\n    <span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>getNamedListByUri<span class=\"token punctuation\">,</span> getTodoListByUri<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"domain/todo\"</span><span class=\"token punctuation\">;</span>\n\n    <span class=\"token keyword\">export</span> <span class=\"token keyword\">default</span> <span class=\"token punctuation\">{</span>\n        props<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n            apiUri<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>type<span class=\"token punctuation\">:</span> String<span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n        <span class=\"token function\">data</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n            <span class=\"token keyword\">return</span> <span class=\"token punctuation\">{</span>\n                <span class=\"token comment\" spellcheck=\"true\">/**\n                 * Holds a reference to the collection for processing and is bound to the screen. Note: we need\n                 * an early binding to 'items' for it to be reactive.\n                 *\n                 * @type TodoCollectionRepresentation\n                 */</span>\n                todoCollection<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n\n        <span class=\"token function\">created</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n\n            log<span class=\"token punctuation\">.</span><span class=\"token function\">debug</span><span class=\"token punctuation\">(</span><span class=\"token template-string\"><span class=\"token string\">`Loading selected todo </span><span class=\"token interpolation\"><span class=\"token interpolation-punctuation punctuation\">${</span><span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>apiUri<span class=\"token interpolation-punctuation punctuation\">}</span></span><span class=\"token string\">`</span></span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n<span class=\"gatsby-highlight-code-line\">            <span class=\"token comment\" spellcheck=\"true\">// get the data from central place see code below!</span>\n</span>            <span class=\"token keyword\">return</span> <span class=\"token function\">getTodoListByUri</span><span class=\"token punctuation\">(</span><span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>$root<span class=\"token punctuation\">.</span>$api<span class=\"token punctuation\">,</span> <span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>apiUri<span class=\"token punctuation\">)</span>\n                <span class=\"token punctuation\">.</span><span class=\"token function\">then</span><span class=\"token punctuation\">(</span>todos <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n                     <span class=\"token comment\" spellcheck=\"true\">// create reference to bind into html</span>\n                     <span class=\"token keyword\">return</span> <span class=\"token keyword\">this</span><span class=\"token punctuation\">.</span>todoCollection <span class=\"token operator\">=</span> todos<span class=\"token punctuation\">;</span>\n                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n                <span class=\"token punctuation\">.</span><span class=\"token keyword\">catch</span><span class=\"token punctuation\">(</span>err <span class=\"token operator\">=</span><span class=\"token operator\">></span> log<span class=\"token punctuation\">.</span><span class=\"token function\">error</span><span class=\"token punctuation\">(</span>err<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n<span class=\"token operator\">&lt;</span><span class=\"token operator\">/</span>script<span class=\"token operator\">></span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Writing the code that walks the network of data is where you need to have the API diagram at hand. A todo list falls in the <code>root > me > todos > todos</code>. But it is a little more complicated because there are singletons (me), named collections and collections of collections in there too. Read through the code and also look at the API itself and see if you can figure it out. This is as hard as it gets! Just note that we not dealing with the semantics of (HTTP) network requests at this level. It is all dealt with in the <code>semantic-network</code> code.</p>\n<p><img src=\"/hypermedia/advanced/todo-api.png\" alt=\"see here\"></p>\n<Instruction>\n<p>Write code that walks the link relations (aka the ‘domain’).</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-js\"  path=\"...todo-hypermedia/client/src/domain/todo.js\"  nocopy=\"false\"><code><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span><span class=\"token keyword\">get</span><span class=\"token punctuation\">,</span> link<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'semantic-network'</span><span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>log<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'logger'</span><span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>findResourceInCollectionByUri<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'semantic-network/utils/collection'</span><span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span>FieldType<span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">'semantic-network/interfaces'</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token comment\" spellcheck=\"true\">/**\n * Get the first level of todos (regardless of tenants)\n *\n * @param {ApiRepresentation} apiResource\n * @param {CacheOptions?} options\n * @returns {Promise&lt;TodoCollectionRepresentation>} sparsely populated\n */</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> getTodoList <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n\n    log<span class=\"token punctuation\">.</span><span class=\"token function\">debug</span><span class=\"token punctuation\">(</span><span class=\"token string\">'Looking for todos on root'</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n    <span class=\"token keyword\">return</span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> <span class=\"token regex\">/me/</span><span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">.</span><span class=\"token function\">then</span><span class=\"token punctuation\">(</span>user <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>user<span class=\"token punctuation\">,</span> <span class=\"token regex\">/todos/</span><span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token comment\" spellcheck=\"true\">/**\n * Get the todos on the todo list\n * @param {TodoCollectionRepresentation} todoCollection\n * @param {CacheOptions?} options\n * @returns {Promise&lt;TodoCollectionRepresentation>}\n */</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> getTodos <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>todoCollection<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n\n    log<span class=\"token punctuation\">.</span><span class=\"token function\">debug</span><span class=\"token punctuation\">(</span><span class=\"token template-string\"><span class=\"token string\">`Looking for todos on list </span><span class=\"token interpolation\"><span class=\"token interpolation-punctuation punctuation\">${</span>link<span class=\"token punctuation\">.</span><span class=\"token function\">getUri</span><span class=\"token punctuation\">(</span>todoCollection<span class=\"token punctuation\">,</span> <span class=\"token string\">'self'</span><span class=\"token punctuation\">)</span><span class=\"token interpolation-punctuation punctuation\">}</span></span><span class=\"token string\">`</span></span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n    <span class=\"token keyword\">return</span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>todoCollection<span class=\"token punctuation\">,</span> <span class=\"token regex\">/todos/</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span><span class=\"token operator\">...</span>options<span class=\"token punctuation\">,</span> includeItems<span class=\"token punctuation\">:</span> <span class=\"token boolean\">true</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token comment\" spellcheck=\"true\">/**\n *\n * Context: (user)-[todos...]\n * Looks for: -[todos...]+->[tags...]\n * @param {TenantCollectionRepresentation} userTenantsCollection\n * @param {CacheOptions?} options\n * @returns {Promise}\n */</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> getTodosWithTagsOnTenantTodos <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>userTenantsCollection<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">return</span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>userTenantsCollection<span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>rel<span class=\"token punctuation\">:</span> <span class=\"token regex\">/todos/</span><span class=\"token punctuation\">,</span> includeItems<span class=\"token punctuation\">:</span> <span class=\"token boolean\">true</span><span class=\"token punctuation\">,</span> batchSize<span class=\"token punctuation\">:</span> <span class=\"token number\">1</span><span class=\"token punctuation\">,</span> <span class=\"token operator\">...</span>options<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">.</span><span class=\"token function\">then</span><span class=\"token punctuation\">(</span>userTenantsTodos <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>\n            userTenantsTodos<span class=\"token punctuation\">,</span>\n            <span class=\"token punctuation\">{</span>\n                <span class=\"token operator\">...</span>options<span class=\"token punctuation\">,</span>\n                iterateOver<span class=\"token punctuation\">:</span> <span class=\"token boolean\">true</span><span class=\"token punctuation\">,</span>\n                rel<span class=\"token punctuation\">:</span> <span class=\"token regex\">/tags/</span><span class=\"token punctuation\">,</span>\n                includeItems<span class=\"token punctuation\">:</span> <span class=\"token boolean\">true</span><span class=\"token punctuation\">,</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token comment\" spellcheck=\"true\">/**\n * Get the todo list items based on a uri starting from the root\n *\n * Context: (api)\n * Looks for: -(me)-[todos...{self:$todoUri}]-[todos...]\n *\n * @param {ApiRepresentation} apiResource\n * @param {string} todoUri\n * @param {CacheOptions?} options\n * @returns {Promise&lt;CollectionRepresentation>}\n */</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> getTodoListByUri <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> todoUri<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n\n    <span class=\"token keyword\">return</span> <span class=\"token function\">getNamedListByUri</span><span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> todoUri<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">.</span><span class=\"token function\">then</span><span class=\"token punctuation\">(</span>itemResource <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token keyword\">get</span><span class=\"token punctuation\">(</span>itemResource<span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span><span class=\"token operator\">...</span>options<span class=\"token punctuation\">,</span> rel<span class=\"token punctuation\">:</span> <span class=\"token regex\">/todos/</span><span class=\"token punctuation\">,</span> includeItems<span class=\"token punctuation\">:</span> <span class=\"token boolean\">true</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token comment\" spellcheck=\"true\">/**\n * Get the name todo list based on a uri starting from the root\n *\n * Context: (api)\n * Looks for: -(me)-[todos...{self:$todoUri}]\n *\n * @param {ApiRepresentation} apiResource\n * @param {string} todoUri\n * @param {CacheOptions?} options\n * @returns {Promise&lt;LinkedRepresentation>}\n */</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> getNamedListByUri <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> todoUri<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span> <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">return</span> <span class=\"token function\">getTodoList</span><span class=\"token punctuation\">(</span>apiResource<span class=\"token punctuation\">,</span> options<span class=\"token punctuation\">)</span>\n        <span class=\"token punctuation\">.</span><span class=\"token function\">then</span><span class=\"token punctuation\">(</span>todosList <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token function\">findResourceInCollectionByUri</span><span class=\"token punctuation\">(</span>todosList<span class=\"token punctuation\">,</span> todoUri<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n</Instruction>",
			"fields": {
				"slug": "/vue-todo-app/3-application-cache/"
			},
			"excerpt": "A client needs an application cache to ensure that there is self-consistency throughout the application (and avoid stale data). Doing this...",
			"frontmatter": {
				"title": "Client-side application cache",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/Users/todd/src/howtohypermedia/content/frontend/vue-todo-app/3-application-cache.md absPath of file",
				"question": "An application cache",
				"answers": [
					"Is a global, single version of what the clients has loaded",
					"Requires that the developers access resources though link relations",
					"Allows binding the UI to representations",
					"All of the above"
				],
				"correctAnswer": 3,
				"description": "Client need to have one copy of representations in the application cache",
				"pageTitle": "Bind views client-side application cache"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Collection Resource",
							"description": "Learn the code mechanics of a edit forms on a collection resource that uses different mime-types"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/11-update-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Update Singleton Resource",
							"description": "Learn the code mechanics of a edit forms on a singleton resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/10-update-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a hypermedia API that works within the constraints of REST"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a hypermedia API "
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "",
							"description": null
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/2-modelling-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Modelling Domain",
							"description": "Learn how to diagram the API textually and get also get visual output"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/3-modelling-domain-link-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Representations",
							"description": "Learn the code mechanics of request/response of representation in an MVC framework with the root API representation containing links and a version"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/4-constructing-representations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Collections",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/6-constructing-collection/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Searchable Resources",
							"description": "Learn the code mechanics of a search forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/8-constructing-search-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Singletons",
							"description": "Learn the code mechanics of a singleton resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/5-constructing-singleton/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Create Resource",
							"description": "Learn the code mechanics of a create forms on a collection"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/9-create-resource/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Constructing Virtual Resource",
							"description": "Learn the code mechanics of a virtual resource with the user resource"
						},
						"fields": {
							"slug": "/aspnetcore-todo-api/7-constructing-viritual/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a todo app and admin functions over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side URLs and routing",
							"description": "Design your application around the holding and reconstructing client state through the URI in the browser location requires matching client views and the data it requires"
						},
						"fields": {
							"slug": "/vue-todo-app/2-client-side-urls-and-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting started",
							"description": "Learn how to build a todo client to use the hypermedia API "
						},
						"fields": {
							"slug": "/vue-todo-app/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Client-side application cache",
							"description": "Client need to have one copy of representations in the application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/3-application-cache/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Updates and deletes",
							"description": "The application cache also deals with the updates and deletion of resources"
						},
						"fields": {
							"slug": "/vue-todo-app/4-updates-and-deletes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "On-demand Authentication",
							"description": "Be able to create and renew authentication tokens when the server requests valid authentication tokens"
						},
						"fields": {
							"slug": "/vue-todo-app/5-on-demand-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to build a todo app over the top of a hypermedia API"
						},
						"fields": {
							"slug": "/vue-todo-app/7-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Cloning Resources",
							"description": "Forms in hypermedia make cloning of trees straightforward and we can leverage that with drag and drop functionality alongside the client-side application cache"
						},
						"fields": {
							"slug": "/vue-todo-app/6-cloning-resources/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "HTML representation",
							"description": "Learn what and why to build HTML representation of data with code on demand"
						},
						"fields": {
							"slug": "/vue-api-browser/2-build-html-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a general purpose client to browse over the API "
						},
						"fields": {
							"slug": "/vue-api-browser/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Generate HTML",
							"description": "Learn how to respond as HTML in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/3-build-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Register text/html",
							"description": "Learn how to respond to HTML content negotiation in ASP.NET Core"
						},
						"fields": {
							"slug": "/vue-api-browser/4-register-html-content-negotiation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GET application/json",
							"description": "Learn how to request JSON from the client"
						},
						"fields": {
							"slug": "/vue-api-browser/5-request-json/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Learn how to user hypermedia as engine to navigate an API and make changes"
						},
						"fields": {
							"slug": "/vue-api-browser/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying with interaction",
							"description": "Learn how to process JSON to make delete, create and update"
						},
						"fields": {
							"slug": "/vue-api-browser/6-displaying-with-interaction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Displaying forms",
							"description": "Learn how to process form values for create and edit forms"
						},
						"fields": {
							"slug": "/vue-api-browser/8-displaying-form-values/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deleting a resource",
							"description": "Learn how to process JSON to try and delete a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/7-try-delete-representation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about hypermedia and why a REST-style of architecture is simple and subtle. It's not really for the lazy or those who like to be clever."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Minimally layered design",
							"description": "Learn why hypermedia is a thin layer across persistence that should guide us through the business problem."
						},
						"fields": {
							"slug": "/basics/1-minimally-layered-design/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Submitting forms",
							"description": "Learn how to process creates (POST) and updates (PUT) on a resource"
						},
						"fields": {
							"slug": "/vue-api-browser/9-submitting-forms/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Trends)",
							"description": "Learn that distributed applications have trended towards data and business logic being processed at the client edge"
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Hypermedia for the dedicated",
							"description": "Learn some techniques that will ease the server and client development"
						},
						"fields": {
							"slug": "/basics/4-for-the-dedicated/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigating a hypermedia API",
							"description": "Learn what a hypermedia looks like to be able to explore a domain."
						},
						"fields": {
							"slug": "/basics/2-navigating-api/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Multiple clients",
							"description": "Learn that most APIs should have multiple types of clients."
						},
						"fields": {
							"slug": "/advanced/0-multiple-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "API Modelling",
							"description": "Learn about how collections and singletons are modelled in hypermedia"
						},
						"fields": {
							"slug": "/advanced/1-api-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Forms Modelling",
							"description": "Links and Forms are used to update resources and controlled by the server that the client then uses"
						},
						"fields": {
							"slug": "/advanced/2-forms-modelling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common questions about these tutorials"
						},
						"fields": {
							"slug": "/advanced/4-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Todo Domain",
							"description": "Learn about domain of the API"
						},
						"fields": {
							"slug": "/advanced/3-todo-domain/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/vue-todo-app/3-application-cache/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/vue-todo-app-3-application-cache.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/vue-todo-app-3-application-cache.json
// module chunks = 7